import React from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { SxProps, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Button from '@mui/material/Button'
import MuiLink from '@mui/material/Link'
import Container from '@mui/material/Container'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Checkbox from '@mui/material/Checkbox'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import ToggleButton from '@mui/material/ToggleButton'
import Tooltip from '@mui/material/Tooltip'
import Popover from '@mui/material/Popover'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import ListIcon from '@mui/icons-material/List'
import LocalCafeIcon from '@mui/icons-material/LocalCafe'

import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings'
import Alert from '../components/alert/alert'
import { useContextHook } from '../store/context'
import { Log } from '../types/log'
import { get, isOsxSafari, setPageTitle } from '../utils/service'
import { toTimeString, toDateString, calculateTimeDiff } from '../utils/dateUtils'
import { getDisplayTemperature } from '../utils/graphUtils'
import { PagedResponse } from '../types/pagedResponse'
import { getLogsUrl } from '../utils/logUtils'
import ListHeader from '../components/listheader/listheader'
import ResponsiveTableHeader from '../components/fields/responsiveTableHeader'
import CompareIcon from '../svgicons/compare'
import CompareView from '../components/compareLogs/compareView'
import { setCuppingRoastIds } from '../store/actions'
import Pagination from '../components/fields/pagination'
import { getLogListHiddenColumns, setLogListHiddenColumns } from '../utils/localStorageUtils'
import { weightValueToDisplay } from '../utils/weightUtils'
import TooltipIconButton from '../components/fields/tooltipIconButton'
import SortableTableHead from '../components/fields/sortableTableHead'
import { SortType } from '../types/enums/sortType'

const NAME_COLUMN = 'name'
const BEAN_COLUMN = 'bean'
const PROFILE_NAME_COLUMN = 'profile_name'
const START_WEIGHT_COLUMN = 'start_weight'
const END_WEIGHT_COLUMN = 'end_weight'
const WHOLE_BEAN_COLOR_COLUMN = 'whole_bean_color'
const GROUND_BEAN_COLOR_COLUMN = 'ground_bean_color'
const DURATION_COLUMN = 'duration'
const FIRST_CRACK_TIME_COLUMN = 'first_crack_time'
const FIRST_CRACK_TEMP_COLUMN = 'first_crack_temp'
const DEV_TIME_COLUMN = 'dev_time'
const END_TEMP_COLUMN = 'end_temp'
const LATEST_CUPPING_SCORE_COLUMN = 'latest_cupping_score'
const FIRST_COMMENT_COLUMN = 'first_comment'

enum ViewMode {
  List = 'list',
  Compare = 'compare',
}

const LogsView = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams()
  const { state, dispatch } = useContextHook()
  const theme = useTheme()
  const location = useLocation()
  const containerRef = React.useRef<HTMLDivElement>(null)
  const locationState = location.state as { compareLogData: Log[] }

  const [logs, setLogs] = React.useState<Array<Log> | null>(null)
  const [loadLogsUrl, setLoadLogsUrl] = React.useState<string>('')
  const [paginationSettings, setPaginationSettings] = React.useState<PagedResponse<Log> | null>(null)
  const [isLoadingLogs, setIsLoadingLogs] = React.useState<boolean>(false)
  const [hasLoadError, setHasLoadError] = React.useState<boolean>(false)
  const [checkedCompareLogs, setCheckedCompareLogs] = React.useState<Array<Log>>([])
  const [viewMode, setViewMode] = React.useState<ViewMode>(ViewMode.List)
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLButtonElement | null>(null)
  const [hiddenColumns, setHiddenColumns] = React.useState<Array<string>>(getLogListHiddenColumns())
  const [sortFragment, setSortFragment] = React.useState<string>('')
  const [searchString, setSearchString] = React.useState<string>('')

  const isScreenBelowMdBreakpoint = useMediaQuery(theme.breakpoints.down('md'))

  React.useEffect(() => {
    // update localStorage when hidden columns array is changed
    setLogListHiddenColumns(hiddenColumns)
  }, [hiddenColumns])

  // load logs whenever loadLogsUrl is changed
  React.useEffect(() => {
    if (loadLogsUrl) {
      setLogs(null)
      setIsLoadingLogs(true)
      setHasLoadError(false)

      get<PagedResponse<Log>>(loadLogsUrl)
        .then((response) => {
          setPaginationSettings(response.data)
          setLogs(response.data.results)
          setIsLoadingLogs(false)
        })
        .catch(() => {
          setIsLoadingLogs(false)
          setHasLoadError(true)
        })
    }
  }, [loadLogsUrl])

  React.useEffect(() => {
    if (state.currentMachine?.id) {
      setLogs(null)
      setPaginationSettings(null)
      setViewMode(ViewMode.List)
      setCheckedCompareLogs([])
      setLoadLogsUrl(getLogsUrl(state.currentMachine.id))
    }
  }, [state.currentMachine?.id]) // reload logs data when a new machine is selected

  React.useEffect(() => {
    if (locationState?.compareLogData) {
      setCheckedCompareLogs(locationState?.compareLogData)
      setViewMode(ViewMode.Compare)
    }
  }, [locationState?.compareLogData])

  // update page title
  React.useEffect(() => {
    if (state.currentMachine?.name) {
      setPageTitle(`${state.currentMachine.name} ${t('logsview.logs')}`)
    }
  }, [state.currentMachine?.name, t])

  // load new resultSet when changing search string or sorting
  React.useEffect(() => {
    if (state.currentMachine?.id) {
      const sortFragmentString = sortFragment ? `&ordering=${sortFragment}` : ''
      setPaginationSettings(null)
      setLoadLogsUrl(`${getLogsUrl(state.currentMachine.id, searchString)}${sortFragmentString}`)
    }
  }, [state.currentMachine?.id, searchString, sortFragment])

  const generateCuppingScoreCell = (log: Log): string | number => {
    if (log.latest_cupping_score) {
      return log.latest_cupping_score.cupping_session_score === null
        ? 'N/A'
        : Math.round(log.latest_cupping_score.cupping_session_score * 100) / 100
    }
    return ''
  }

  const getCheckboxStyling = (isChecked: boolean): SxProps => {
    return {
      boxShadow: isChecked ? `0px 0px 0px 25px inset ${theme.palette.primary.main}` : undefined,
      color: isChecked ? `${theme.palette.primary.contrastText} !important` : undefined,
      borderRadius: '4px',
    }
  }

  const isVisibleColumn = (columnName: string): boolean => {
    return hiddenColumns.indexOf(columnName) === -1
  }

  const onSort = (newSortFragment: string): void => {
    setSortFragment(newSortFragment)
  }

  const generateTableRows = (): Array<JSX.Element> => {
    let prevDate = ''
    return (logs || []).map((log) => {
      const startDateString = toDateString(log.start_timestamp)
      const shouldRenderHeaderRow = startDateString !== prevDate
      const isCheckedForCompare = !!checkedCompareLogs.find((x) => x.url === log.url)
      const isCheckedForCupping = !!(state.cuppingRoastIds || []).find((url) => url === log.url)
      prevDate = startDateString
      return (
        <React.Fragment key={log.url}>
          {shouldRenderHeaderRow && (
            <TableRow>
              <TableCell colSpan={100}>{startDateString}</TableCell>
            </TableRow>
          )}
          <TableRow className="colored-row">
            <TableCell>
              <ResponsiveTableHeader header={t('logsview.time')} />
              {toTimeString(log.start_timestamp)}
            </TableCell>
            <TableCell>
              <ResponsiveTableHeader header={t('logsview.batch')} />
              {isOsxSafari() ? (
                <MuiLink component={Link} to={`/machine/${params.machineId}/logs/${log.id}/`}>
                  {`#${log.batch_no}`}
                </MuiLink>
              ) : (
                <>
                  <Button
                    className="link-cell"
                    sx={{
                      right: isScreenBelowMdBreakpoint ? 0 : '176px !important',
                      bottom: isScreenBelowMdBreakpoint ? '58px !important' : 0,
                    }}
                    component={Link}
                    to={`/machine/${params.machineId}/logs/${log.id}/`}
                  />
                  {`#${log.batch_no}`}
                </>
              )}
            </TableCell>
            {isVisibleColumn(NAME_COLUMN) && (
              <TableCell>
                <ResponsiveTableHeader header={t('logsview.name')} />
                {log.name || ''}
              </TableCell>
            )}
            {isVisibleColumn(BEAN_COLUMN) && (
              <TableCell>
                <ResponsiveTableHeader header={t('logsview.beanname')} />
                {log.inventory_name || log.bean_name}
              </TableCell>
            )}
            {isVisibleColumn(PROFILE_NAME_COLUMN) && (
              <TableCell>
                <ResponsiveTableHeader header={t('logsview.profilename')} />
                {log.profile_name || log.profile_data?.name}
              </TableCell>
            )}
            {isVisibleColumn(START_WEIGHT_COLUMN) && (
              <TableCell>
                <ResponsiveTableHeader header={t('logsview.startweight')} />
                {log.start_weight ? `${weightValueToDisplay(log.start_weight)}` : ''}
              </TableCell>
            )}
            {isVisibleColumn(END_WEIGHT_COLUMN) && (
              <TableCell>
                <ResponsiveTableHeader header={t('logsview.endweight')} />
                {log.end_weight ? `${weightValueToDisplay(log.end_weight)}` : ''}
              </TableCell>
            )}
            {isVisibleColumn(WHOLE_BEAN_COLOR_COLUMN) && (
              <TableCell>
                <ResponsiveTableHeader header={t('logsview.wholebeancolor')} />
                {log.whole_bean_color}
              </TableCell>
            )}
            {isVisibleColumn(GROUND_BEAN_COLOR_COLUMN) && (
              <TableCell>
                <ResponsiveTableHeader header={t('logsview.groundbeancolor')} />
                {log.ground_bean_color}
              </TableCell>
            )}
            {isVisibleColumn(DURATION_COLUMN) && (
              <TableCell>
                <ResponsiveTableHeader header={t('logsview.duration')} />
                {calculateTimeDiff(log.drop_timestamp || log.end_timestamp, log.start_timestamp)}
              </TableCell>
            )}
            {isVisibleColumn(FIRST_CRACK_TIME_COLUMN) && (
              <TableCell>
                <ResponsiveTableHeader header={t('logsview.firstcracktime')} />
                {calculateTimeDiff(log.firstcrack_timestamp, log.start_timestamp)}
              </TableCell>
            )}
            {isVisibleColumn(FIRST_CRACK_TEMP_COLUMN) && (
              <TableCell>
                <ResponsiveTableHeader header={t('logsview.firstcracktemp')} />
                {getDisplayTemperature(log.fc_temp) || 'N/A'}
              </TableCell>
            )}
            {isVisibleColumn(DEV_TIME_COLUMN) && (
              <TableCell>
                <ResponsiveTableHeader header={t('logsview.developmenttime')} />
                {calculateTimeDiff(log.drop_timestamp || log.end_timestamp, log.firstcrack_timestamp)}
              </TableCell>
            )}
            {isVisibleColumn(END_TEMP_COLUMN) && (
              <TableCell>
                <ResponsiveTableHeader header={t('logsview.endtemp')} />
                {getDisplayTemperature(log.end_temp) || 'N/A'}
              </TableCell>
            )}
            {isVisibleColumn(LATEST_CUPPING_SCORE_COLUMN) && (
              <TableCell>
                <ResponsiveTableHeader header={t('logsview.latestcuppingscore')} />
                {generateCuppingScoreCell(log)}
              </TableCell>
            )}
            {isVisibleColumn(FIRST_COMMENT_COLUMN) && (
              <TableCell>
                <ResponsiveTableHeader header={t('logsview.firstcomment')} />
                {log.first_comment?.comment || ''}
              </TableCell>
            )}
            <TableCell className="splitter-cell" />
            <TableCell className="action-cell" sx={{ textAlign: 'center' }}>
              <Tooltip title={t(isCheckedForCupping ? 'logsview.deselectforcupping' : 'logsview.selectforcupping')}>
                <Checkbox
                  checked={isCheckedForCupping}
                  icon={<LocalCafeIcon />}
                  checkedIcon={<LocalCafeIcon />}
                  inputProps={{
                    'aria-label': t(isCheckedForCupping ? 'logsview.deselectforcupping' : 'logsview.selectforcupping'),
                  }}
                  onChange={(_event: React.ChangeEvent<HTMLInputElement>, newCheckedState: boolean) => {
                    if (newCheckedState) {
                      dispatch(setCuppingRoastIds([...(state.cuppingRoastIds || []), log.url]))
                    } else {
                      dispatch(setCuppingRoastIds((state.cuppingRoastIds || []).filter((url) => url !== log.url)))
                    }
                  }}
                  sx={getCheckboxStyling(isCheckedForCupping)}
                />
              </Tooltip>
            </TableCell>
            <TableCell className="splitter-cell" />
            <TableCell className="action-cell" sx={{ textAlign: 'center' }}>
              <Tooltip title={t(isCheckedForCompare ? 'logsview.deselectforcompare' : 'logsview.selectforcompare')}>
                <Checkbox
                  checked={isCheckedForCompare}
                  icon={<CompareIcon />}
                  checkedIcon={<CompareIcon />}
                  inputProps={{
                    'aria-label': t(isCheckedForCompare ? 'logsview.deselectforcompare' : 'logsview.selectforcompare'),
                  }}
                  onChange={(_event: React.ChangeEvent<HTMLInputElement>, newCheckedState: boolean) => {
                    if (newCheckedState) {
                      setCheckedCompareLogs((oldCheckedLogs) => [...oldCheckedLogs, log])
                    } else {
                      setCheckedCompareLogs((oldCheckedLogs) => oldCheckedLogs.filter((x) => x.url !== log.url))
                    }
                  }}
                  sx={getCheckboxStyling(isCheckedForCompare)}
                />
              </Tooltip>
            </TableCell>
          </TableRow>
        </React.Fragment>
      )
    })
  }

  return (
    <Container
      component="main"
      maxWidth={false}
      ref={containerRef}
      sx={{ display: 'flex', flex: 1, overflow: viewMode === ViewMode.Compare ? 'hidden' : undefined }}
    >
      <Popover
        open={!!menuAnchorEl}
        anchorEl={menuAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={() => setMenuAnchorEl(null)}
      >
        <Stack sx={{ p: 2 }}>
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend">{t('logsview.showcolumns')}</FormLabel>
            <FormGroup sx={{ alignItems: 'flex-start' }}>
              {[
                { id: 'STATIC_TIME', label: t('logsview.time'), disabled: true },
                { id: 'STATIC_BATCH', label: t('logsview.batch'), disabled: true },
                { id: NAME_COLUMN, label: t('logsview.name'), disabled: false },
                { id: BEAN_COLUMN, label: t('logsview.beanname'), disabled: false },
                { id: PROFILE_NAME_COLUMN, label: t('logsview.profilename'), disabled: false },
                { id: START_WEIGHT_COLUMN, label: t('logsview.startweight'), disabled: false },
                { id: END_WEIGHT_COLUMN, label: t('logsview.endweight'), disabled: false },
                { id: WHOLE_BEAN_COLOR_COLUMN, label: t('logsview.wholebeancolor'), disabled: false },
                { id: GROUND_BEAN_COLOR_COLUMN, label: t('logsview.groundbeancolor'), disabled: false },
                { id: DURATION_COLUMN, label: t('logsview.duration'), disabled: false },
                { id: FIRST_CRACK_TIME_COLUMN, label: t('logsview.firstcracktime'), disabled: false },
                { id: FIRST_CRACK_TEMP_COLUMN, label: t('logsview.firstcracktemp'), disabled: false },
                { id: DEV_TIME_COLUMN, label: t('logsview.developmenttime'), disabled: false },
                { id: END_TEMP_COLUMN, label: t('logsview.endtemp'), disabled: false },
                { id: LATEST_CUPPING_SCORE_COLUMN, label: t('logsview.latestcuppingscore'), disabled: false },
                { id: FIRST_COMMENT_COLUMN, label: t('logsview.firstcomment'), disabled: false },
                { id: 'STATIC_CUPPING', label: t('logsview.cupping'), disabled: true },
                { id: 'STATIC_COMPARE', label: t('logsview.compare'), disabled: true },
              ].map((x) => {
                return (
                  <FormControlLabel
                    key={x.id}
                    control={
                      <Checkbox
                        checked={isVisibleColumn(x.id)}
                        disabled={x.disabled}
                        onChange={(_event, isChecked) => {
                          setHiddenColumns((oldHiddenColumns) => {
                            return !isChecked ? [...oldHiddenColumns, x.id] : oldHiddenColumns.filter((y) => y !== x.id)
                          })
                        }}
                      />
                    }
                    label={x.label}
                  />
                )
              })}
            </FormGroup>
          </FormControl>
        </Stack>
      </Popover>
      <Stack sx={{ flex: 1, overflow: viewMode === ViewMode.Compare ? 'hidden' : undefined, display: 'flex' }}>
        {state.currentMachine?.name && (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            width="100%"
            spacing={2}
          >
            <ListHeader
              headerText={`${state.currentMachine.name} ${t('logsview.logs')}`}
              searchPlaceholder={t('logsview.searchlogs')}
              typeString={t('logsview.logs')}
              totalResults={paginationSettings?.count || 0}
              isSearchFieldHidden={viewMode === ViewMode.Compare}
              onSearch={(newSearchString: string): void => {
                if (state.currentMachine?.id && searchString !== newSearchString) {
                  setSearchString(newSearchString)
                }
              }}
            />
            <Stack direction="row" justifyContent="space-between">
              <ToggleButtonGroup
                color="primary"
                size="small"
                exclusive
                value={viewMode}
                onChange={(_event: React.MouseEvent<HTMLElement>, newViewMode: ViewMode) => setViewMode(newViewMode)}
              >
                <ToggleButton value={ViewMode.List} disabled={viewMode === ViewMode.List} sx={{ width: 135 }}>
                  <ListIcon sx={{ mr: 1 }} />
                  {t('logsview.list')}
                </ToggleButton>
                <ToggleButton
                  value={ViewMode.Compare}
                  disabled={viewMode === ViewMode.Compare || checkedCompareLogs.length === 0}
                  sx={{ width: 135 }}
                >
                  <CompareIcon sx={{ mr: 1 }} />
                  {`${t('logsview.compare')} ${checkedCompareLogs.length > 0 ? `(${checkedCompareLogs.length})` : ''}`}
                </ToggleButton>
              </ToggleButtonGroup>
              <TooltipIconButton
                tooltip={t('logsview.showhidecolumns')}
                icon={<DisplaySettingsIcon />}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => setMenuAnchorEl(event.currentTarget)}
              />
            </Stack>
          </Stack>
        )}
        {viewMode === ViewMode.List && (
          <Stack spacing={2} width="100%" sx={{ mb: 4 }}>
            {logs && logs.length > 0 && (
              <TableContainer>
                <Table className="colored-table collapse-table">
                  <TableHead>
                    <TableRow>
                      <SortableTableHead
                        sortFragment={sortFragment}
                        sortProp="start_timestamp"
                        sortType={SortType.Date}
                        onSort={onSort}
                      >
                        {t('logsview.time')}
                      </SortableTableHead>
                      <SortableTableHead
                        sortFragment={sortFragment}
                        sortProp="batch_no"
                        sortType={SortType.Number}
                        onSort={onSort}
                      >
                        {t('logsview.batch')}
                      </SortableTableHead>
                      {isVisibleColumn(NAME_COLUMN) && (
                        <SortableTableHead
                          sortFragment={sortFragment}
                          sortProp="name"
                          sortType={SortType.String}
                          onSort={onSort}
                        >
                          {t('logsview.name')}
                        </SortableTableHead>
                      )}
                      {isVisibleColumn(BEAN_COLUMN) && (
                        <SortableTableHead
                          sortFragment={sortFragment}
                          sortProp="inventory_name"
                          sortType={SortType.String}
                          onSort={onSort}
                        >
                          {t('logsview.beanname')}
                        </SortableTableHead>
                      )}
                      {isVisibleColumn(PROFILE_NAME_COLUMN) && (
                        <SortableTableHead
                          sortFragment={sortFragment}
                          sortProp="profile_name"
                          sortType={SortType.String}
                          onSort={onSort}
                        >
                          {t('logsview.profilename')}
                        </SortableTableHead>
                      )}
                      {isVisibleColumn(START_WEIGHT_COLUMN) && (
                        <SortableTableHead
                          sortFragment={sortFragment}
                          sortProp="start_weight"
                          sortType={SortType.Number}
                          onSort={onSort}
                        >
                          {t('logsview.startweight')}
                        </SortableTableHead>
                      )}
                      {isVisibleColumn(END_WEIGHT_COLUMN) && (
                        <SortableTableHead
                          sortFragment={sortFragment}
                          sortProp="end_weight"
                          sortType={SortType.Number}
                          onSort={onSort}
                        >
                          {t('logsview.endweight')}
                        </SortableTableHead>
                      )}
                      {isVisibleColumn(WHOLE_BEAN_COLOR_COLUMN) && (
                        <SortableTableHead
                          sortFragment={sortFragment}
                          sortProp="whole_bean_color"
                          sortType={SortType.Number}
                          onSort={onSort}
                        >
                          {t('logsview.wholebeancolor')}
                        </SortableTableHead>
                      )}
                      {isVisibleColumn(GROUND_BEAN_COLOR_COLUMN) && (
                        <SortableTableHead
                          sortFragment={sortFragment}
                          sortProp="ground_bean_color"
                          sortType={SortType.Number}
                          onSort={onSort}
                        >
                          {t('logsview.groundbeancolor')}
                        </SortableTableHead>
                      )}
                      {isVisibleColumn(DURATION_COLUMN) && (
                        <SortableTableHead
                          sortFragment={sortFragment}
                          sortProp="batch_duration"
                          sortType={SortType.Duration}
                          onSort={onSort}
                        >
                          {t('logsview.duration')}
                        </SortableTableHead>
                      )}
                      {isVisibleColumn(FIRST_CRACK_TIME_COLUMN) && (
                        <SortableTableHead
                          sortFragment={sortFragment}
                          sortProp="fc_time"
                          sortType={SortType.Timestamp}
                          onSort={onSort}
                        >
                          {t('logsview.firstcracktime')}
                        </SortableTableHead>
                      )}
                      {isVisibleColumn(FIRST_CRACK_TEMP_COLUMN) && (
                        <TableCell>{t('logsview.firstcracktemp')}</TableCell>
                      )}
                      {isVisibleColumn(DEV_TIME_COLUMN) && (
                        <SortableTableHead
                          sortFragment={sortFragment}
                          sortProp="dev_time"
                          sortType={SortType.Duration}
                          onSort={onSort}
                        >
                          {t('logsview.developmenttime')}
                        </SortableTableHead>
                      )}
                      {isVisibleColumn(END_TEMP_COLUMN) && <TableCell>{t('logsview.endtemp')}</TableCell>}
                      {isVisibleColumn(LATEST_CUPPING_SCORE_COLUMN) && (
                        <TableCell>{t('logsview.latestcuppingscore')}</TableCell>
                      )}
                      {isVisibleColumn(FIRST_COMMENT_COLUMN) && <TableCell>{t('logsview.firstcomment')}</TableCell>}
                      <TableCell className="splitter-cell"></TableCell>
                      <TableCell className="action-cell-header">{t('logsview.cupping')}</TableCell>
                      <TableCell className="splitter-cell"></TableCell>
                      <TableCell className="action-cell-header">{t('logsview.compare')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{generateTableRows()}</TableBody>
                </Table>
              </TableContainer>
            )}
            {isLoadingLogs && <CircularProgress />}
            {logs && logs.length === 0 && <Alert severity="info">{t('logsview.nologs')}</Alert>}
            {hasLoadError && <Alert severity="error">{t('logsview.loaderror')}</Alert>}
            {paginationSettings && (
              <Pagination
                isLoading={isLoadingLogs}
                paginationSettings={paginationSettings}
                onChangePage={(newPageUrl: string) => setLoadLogsUrl(newPageUrl)}
              />
            )}
          </Stack>
        )}

        {viewMode === ViewMode.Compare && state.currentMachine && (
          <Stack sx={{ height: '100%', flex: 1, overflow: 'hidden' }}>
            <CompareView
              onDeselectLog={(logIdToRemove: number) =>
                setCheckedCompareLogs((oldLogs) => oldLogs.filter((x) => x.id !== logIdToRemove))
              }
              selectedLogs={checkedCompareLogs}
              machine={state.currentMachine}
              user={state.user}
            />
          </Stack>
        )}
      </Stack>
    </Container>
  )
}

export default LogsView
